import { Info } from 'lucide-react';
import { NULL_VALUE } from 'constants/displayConstants';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { OperationalPeriodScheduleTrigger } from '../OperationalPeriodScheduleControl';
import { Ics204ReadOnlyField } from './Ics204Fields';
import { ICS204_BOX_LABELS, Ics204CardSection } from './ics204BoxLabels';
import { useIcs204HeaderContext } from './useIcs204HeaderContext';

const INCIDENT_NAME_READONLY_TOOLTIP =
  'This section is read-only. It is automatically filled from the incident name.';

const OPERATIONAL_PERIOD_READONLY_TOOLTIP =
  'Click to set the scheduled start and end of the next operational period for this incident workspace. Working-period forms print that window.';

type ReadOnlySectionInfoProps = {
  ariaLabel: string;
  testId: string;
  tooltip: string;
};

function ReadOnlySectionInfo({
  ariaLabel,
  testId,
  tooltip,
}: ReadOnlySectionInfoProps) {
  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger
          render={
            <span
              role="button"
              tabIndex={0}
              aria-label={ariaLabel}
              className="tw:inline-flex tw:shrink-0 tw:text-[var(--text-weak)]"
              data-testid={testId}
              onClick={(e) => e.stopPropagation()}
              onPointerDown={(e) => e.stopPropagation()}
              onKeyDown={(e) => e.stopPropagation()}
            >
              <Info className="tw:h-3.5 tw:w-3.5" />
            </span>
          }
        />
        <TooltipContent side="bottom" className="tw:max-w-xs">
          {tooltip}
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}

type Ics204IncidentPeriodHeaderProps = {
  periodNumber: number;
};

/**
 * ICS-204 Boxes 1 and 2 (incident name + operational period From/To). The
 * period box prints the window and opens the workspace schedule. Intended to
 * sit beside Box 3 in the card’s top row.
 */
export function Ics204IncidentPeriodHeader({
  periodNumber,
}: Ics204IncidentPeriodHeaderProps) {
  const { incidentName, opFrom, opTo } = useIcs204HeaderContext(periodNumber);

  return (
    <>
      <Ics204CardSection
        boxLabel={ICS204_BOX_LABELS.incidentName}
        testId="ics204-section-incident-name"
        className="tw:mt-0 tw:h-full"
        headerAction={
          <ReadOnlySectionInfo
            ariaLabel="About Incident Name"
            testId="ics204-incident-name-info"
            tooltip={INCIDENT_NAME_READONLY_TOOLTIP}
          />
        }
      >
        <span
          className="tw:text-foreground"
          data-testid="ics204-field-incident-name"
        >
          {incidentName.trim() ? incidentName : NULL_VALUE}
        </span>
      </Ics204CardSection>
      <Ics204CardSection
        boxLabel={ICS204_BOX_LABELS.operationalPeriod}
        testId="ics204-section-operational-period"
        className="tw:mt-0 tw:h-full"
        headerAction={
          <ReadOnlySectionInfo
            ariaLabel="About Operational Period"
            testId="ics204-operational-period-info"
            tooltip={OPERATIONAL_PERIOD_READONLY_TOOLTIP}
          />
        }
      >
        <OperationalPeriodScheduleTrigger testId="ics204-operational-period">
          <div className="tw:grid tw:grid-cols-1 tw:gap-3 tw:sm:grid-cols-2">
            <Ics204ReadOnlyField
              label="From:"
              value={opFrom}
              testId="ics204-field-op-from"
            />
            <Ics204ReadOnlyField
              label="To:"
              value={opTo}
              testId="ics204-field-op-to"
            />
          </div>
        </OperationalPeriodScheduleTrigger>
      </Ics204CardSection>
    </>
  );
}
